// Типизированный реестр событий аналитики. Полный список — docs/product/spec/09_tech.md §5.
// Хардкод строк-названий событий в компонентах запрещён — только через ANALYTICS_EVENTS.

export type AnalyticsEvent<TParams extends Record<string, unknown> = Record<string, unknown>> = {
  name: string
  params: TParams
}

/**
 * Тип, который принимают `analytics.track` и `useAnalytics().track`.
 * Намеренно широкий: событие с любыми параметрами должно быть ему присваиваемо.
 */
export type AnyAnalyticsEvent = AnalyticsEvent<Record<string, unknown>>

// Пустой объект принимается опционально: и `event()`, и `event({})` валидны.
type EventFactory = (params?: Record<string, never>) => AnalyticsEvent<Record<string, never>>

type EventWithParamsFactory<TParams extends Record<string, unknown>> = (
  params: TParams,
) => AnalyticsEvent<TParams>

/** Событие без параметров — фабрика, не принимающая аргументов */
const event =
  <TName extends string>(name: TName): EventFactory =>
  () => ({ name, params: {} as Record<string, never> })

/** Событие с параметрами */
const eventWithParams =
  <TName extends string, TParams extends Record<string, unknown>>(
    name: TName,
  ): EventWithParamsFactory<TParams> =>
  (params: TParams) => ({ name, params })

export const ANALYTICS_EVENTS = {
  // Онбординг ------------------------------------------------------------
  onbStart: event('onb_start'),
  onbNameEdited: event('onb_name_edited'),
  onbGenderAsked: event('onb_gender_asked'),
  onbBirthdateSet: event('onb_birthdate_set'),
  onbBirthtimeSet: event('onb_birthtime_set'),
  onbCitySet: event('onb_city_set'),
  onbSkipTime: event('onb_skip_time'),
  onbSkipCity: event('onb_skip_city'),
  onbValueShown: event('onb_value_shown'),
  onbInviteOpened: event('onb_invite_opened'),
  onbInviteSent: event('onb_invite_sent'),
  onbInviteSkipped: event('onb_invite_skipped'),
  onbDone: event('onb_done'),
  onbB0Shown: event('onb_b0_shown'),

  // Главная ----------------------------------------------------------------
  mainShown: event('main_shown'),
  forecastAnswered: eventWithParams<'forecast_answered', { answer: 'hit' | 'miss' }>(
    'forecast_answered',
  ),
  forecastShared: event('forecast_shared'),
  moodSet: eventWithParams<'mood_set', { value: 1 | 2 | 3 | 4 }>('mood_set'),
  linksWidgetTap: event('links_widget_tap'),
  natalCtaTap: event('natal_cta_tap'),
  eveningNudgeShown: event('evening_nudge_shown'),

  // Узы ----------------------------------------------------------------------
  canvasShown: event('canvas_shown'),
  nodeTap: event('node_tap'),
  linkCreated: eventWithParams<'link_created', { type: string }>('link_created'),
  linkInviteSent: event('link_invite_sent'),
  linkInviteRevoked: event('link_invite_revoked'),
  linkActivated: eventWithParams<'link_activated', { type: string }>('link_activated'),
  /**
   * Переход по ссылке-приглашению доехал до сервера и чем-то кончился.
   *
   * `outcome` — значение `InviteOutcome` из contracts либо `FAILED` (сбой у нас). Событие
   * закрывает главную дыру воронки: до него «ссылку не открывали» и «открыли, но связь не
   * создалась» выглядели в данных одинаково.
   */
  inviteAccepted: eventWithParams<'invite_accepted', { outcome: string }>('invite_accepted'),
  linkRemoved: event('link_removed'),
  raySent: eventWithParams<'ray_sent', { hasText: boolean; complimentId?: string }>('ray_sent'),
  beaconShown: event('beacon_shown'),
  beaconTap: event('beacon_tap'),
  beaconFired: event('beacon_fired'),
  beaconSelfWarned: event('beacon_self_warned'),
  inboxOpened: event('inbox_opened'),
  inboxItemTap: eventWithParams<'inbox_item_tap', { kind: string }>('inbox_item_tap'),
  limitReachedShown: event('limit_reached_shown'),
  limitCtaTap: event('limit_cta_tap'),

  // Совместимость ------------------------------------------------------------
  compatTypeSelected: eventWithParams<'compat_type_selected', { type: string }>(
    'compat_type_selected',
  ),
  compatNodePrefill: event('compat_node_prefill'),
  compatSignMode: event('compat_sign_mode'),
  compatCalculated: eventWithParams<
    'compat_calculated',
    { type: string; hasTime: boolean; score: number }
  >('compat_calculated'),
  compatShared: event('compat_shared'),
  compatRepeat: event('compat_repeat'),
  compatDeepenTap: event('compat_deepen_tap'),
  compatHistoryShown: event('compat_history_shown'),

  // Натальная карта ------------------------------------------------------------
  natalIntroShown: event('natal_intro_shown'),
  natalDataSubmitted: eventWithParams<
    'natal_data_submitted',
    { hasTime: boolean; hasCity: boolean }
  >('natal_data_submitted'),
  natalCalculated: event('natal_calculated'),
  natalHousesOpened: event('natal_houses_opened'),
  natalShared: event('natal_shared'),
  // Карта другому человеку — отдельная ветка `/natal/other` (правка основателя №11).
  natalOtherStarted: event('natal_other_started'),
  natalOtherCalculated: eventWithParams<
    'natal_other_calculated',
    { hasTime: boolean; hasCity: boolean }
  >('natal_other_calculated'),

  // Шэринг ------------------------------------------------------------
  /**
   * Telegram не отправил готовое сообщение с карточкой. `reason` — его собственная причина
   * (`USER_DECLINED`, `MESSAGE_EXPIRED`, `MESSAGE_SEND_FAILED`, …), без неё «сообщение в чате
   * с красной иконкой» неотличимо от «человек передумал и закрыл выбор чата».
   */
  shareMessageFailed: eventWithParams<'share_message_failed', { kind: string; reason: string }>(
    'share_message_failed',
  ),

  // Статистика и профиль ------------------------------------------------------------
  statsShown: event('stats_shown'),
  hitrateSheetOpened: event('hitrate_sheet_opened'),
  forecastArchiveShare: event('forecast_archive_share'),
  natalDeepenTap: event('natal_deepen_tap'),
  profileShown: event('profile_shown'),
  vacationOn: eventWithParams<'vacation_on', { until: string }>('vacation_on'),
  vacationOff: event('vacation_off'),
  forecastTimeSet: eventWithParams<'forecast_time_set', { t: string }>('forecast_time_set'),
  notifToggled: eventWithParams<'notif_toggled', { type: string }>('notif_toggled'),
  bowlShown: eventWithParams<'bowl_shown', { source: string }>('bowl_shown'),
  bowlFeedTap: event('bowl_feed_tap'),
  bowlLoveAnyway: event('bowl_love_anyway'),
  paywallShown: event('paywall_shown'),
  paywallPlanTap: eventWithParams<'paywall_plan_tap', { plan: string }>('paywall_plan_tap'),
  // Оплаты на MVP нет: вместо неё — записанное намерение и экран благодарности. Событие нужно,
  // чтобы конверсию было видно и в аналитике, а не только в таблице `subscription_intents`.
  paywallThanksShown: event('paywall_thanks_shown'),
  birthDataUpdated: event('birth_data_updated'),
  privacyShown: event('privacy_shown'),
  dataDeleteRequested: event('data_delete_requested'),
  // Акцепт оферты. `source` повторяет то, что уходит в запись на сервере: `onboarding` —
  // строка под кнопкой первого экрана, `resume` — шторка для тех, кто зарегистрировался
  // до появления документа. Событие нужно не для воронки, а чтобы видеть, сколько уже
  // зарегистрированных людей действительно доходят до шторки и нажимают в ней кнопку.
  termsAccepted: eventWithParams<'terms_accepted', { source: string }>('terms_accepted'),
  termsSheetShown: event('terms_sheet_shown'),
} as const
